import { ElementModel, ElementModelData } from "@courses-pm2/api/src/ts/models/ElementModel";

/**
 * Specification of access codes which describe the reasons that the user isn't
 * authorized to view this piece of content.
 */
export enum AccessCodes {
    /**
     * User must be authenticated (signed in) to access the content.
     */
    AUTHENTICATION_REQUIRED = 1,

    /**
     * User must be authorized (own a license) to access the content.
     */
    LICENSE_REQUIRED = 2,

    /**
     * User must be sign in and own a license to access the content.
     */
    LICENSE_REQUIRED_AUTHENTICATION_REQUIRED = 3,

    /**
     * Authenticated users are not allowed access to the content.
     */
    GUEST_ONLY = 4,

    /**
     * Students are not allowed access to the content.
     */
    STUDENT_FORBIDDEN = 8,

    /**
     * Teachers are not allowed access to the content.
     */
    TEACHER_FORBIDDEN = 16,

    /**
     * A skoletube license is required to access the content.
     */
    SKOLETUBE_REQUIRED = 32,

    /**
     * Skoletube should not be available to access the content.
     */
    SKOLETUBE_FORBIDDEN = 64,
}

/**
 * If for some reason a user isn't authorized to view some requested content,
 * this model is used as a stub where the actual content model would have been
 * returned.
 *
 * This way clients can implement custom ui in places where content elements
 * cannot be displayed for the current user.
 */
export class UnauthorizedElementModel extends ElementModel {
    /**
     * Specifies the reason why the user isn't authorized to view this piece of
     * content.
     */
    public readonly accessCode: number;

    /**
     * Specifies the type of the element that the user wasn't authorized to
     * view.
     */
    public readonly elementType: string;

    /**
     * Apply data when the model is being instantiated
     */
    constructor(initial: ElementModelData & {
        elementType: string;
        accessCode: number;
    }) {
        super(initial);

        this.accessCode = initial.accessCode;
        this.elementType = initial.elementType;
    }

    /**
     * Preview is unavailable for unauthorized elements (there is nothing to be
     * displayed anyway).
     */
    public async getPreviewImageUrl(): Promise<undefined> {
        return undefined;
    }

    /**
     * Preview is unavailable for unauthorized elements (there is nothing to be
     * displayed anyway).
     */
    public getPreviewText(): undefined {
        return undefined;
    }

    /**
     * @inheritdoc
     *
     * Don't include elements that the user isn't authorized to view when
     * calculating standard page count.
     */
    // tslint:disable-next-line: completed-docs
    public getStandardPageCount(): number {
        return 0;
    }
}
